import { Link } from "react-router-dom";
import {
  ArrowRight,
  UserCircle,
  FileText,
  Video,
  Package,
  Handshake,
  QrCode,
  CheckCircle2,
  Sparkles,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";

/**
 * How It Works — five-step visual story of the Inter-View product.
 * Each step is its own card with a clean icon, ordinal index, short
 * title, and supporting body copy. Designed to be scannable on a
 * pitch slide and detail-rich for an interested visitor.
 */

const STEPS = [
  {
    n: "01",
    icon: UserCircle,
    title: "Build your profile",
    blurb:
      "Start with the basics — name, contact, professional title, a short bio, and a profile photo. This becomes the canonical source of truth for everything below.",
    bullets: [
      "Edit once, syncs everywhere",
      "Profile photo with pan + zoom crop",
      "Saved signature for cover letters",
    ],
    accent: "from-blue-500 to-blue-600",
  },
  {
    n: "02",
    icon: FileText,
    title: "Create your resume and cover letter",
    blurb:
      "Pick a clean template, fill in your experience, and write a cover letter in your own voice. Everything pulls from your profile, so it stays consistent.",
    bullets: [
      "Polished, recruiter-friendly templates",
      "Cover letter PDF with your saved signature",
      "Resume and cover letter stay in sync",
    ],
    accent: "from-indigo-500 to-blue-600",
  },
  {
    n: "03",
    icon: Video,
    title: "Record your short video introduction",
    blurb:
      "Answer five short prompts in your own words. We trim silences, add transitions, and stitch them into a clean, professional 60-second introduction.",
    bullets: [
      "Guided 5-question recording flow",
      "Review and re-record each segment",
      "Auto-stitched with branded outro",
    ],
    accent: "from-violet-500 to-indigo-600",
  },
  {
    n: "04",
    icon: Package,
    title: "Generate one printable application package",
    blurb:
      "We combine your cover letter and resume into a single application package, and add a QR code that links recruiters straight to your video introduction.",
    bullets: [
      "Single PDF: cover letter → resume",
      "Branded QR code linking to your video page",
      "Recruiter-ready download or print",
    ],
    accent: "from-emerald-500 to-teal-600",
  },
  {
    n: "05",
    icon: Handshake,
    title: "Apply in person with confidence",
    blurb:
      "Hand the printed package over the counter. If the hiring manager isn't there, they can scan the QR code later and meet you on video — same first impression, every time.",
    bullets: [
      "A real first impression — even when the manager is out",
      "Recruiter page works on any phone",
      "Stand out from one-page-resume applicants",
    ],
    accent: "from-amber-500 to-orange-600",
  },
];

export default function HowItWorksPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white" data-testid="how-it-works-page">
      <SiteHeader />

      {/* Hero */}
      <section className="pt-16 pb-12 lg:pt-24 lg:pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="mb-6 bg-blue-100 text-blue-800 hover:bg-blue-200">
            <Sparkles className="h-3.5 w-3.5 mr-1.5" /> How Inter-View works
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
            From profile to package in
            <span className="text-blue-600"> five steps</span>.
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
            Inter-View helps applicants introduce themselves to employers in a more professional,
            memorable, and complete way — even when the manager isn't available in person.
          </p>
        </div>
      </section>

      {/* Step rail summary */}
      <section className="pb-2">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <ol className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4" data-testid="how-it-works-rail">
            {STEPS.map((s) => (
              <li
                key={s.n}
                className="flex flex-col items-center text-center p-3 rounded-xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${s.accent} text-white text-xs font-bold flex items-center justify-center mb-1.5`}>
                  {s.n}
                </div>
                <p className="text-xs sm:text-sm font-semibold text-gray-700 leading-tight">
                  {s.title}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Steps */}
      <section className="py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative space-y-8 lg:space-y-12">
            {/* Vertical thread between desktop step cards. Subtle, so
                it doesn't compete with the card content. */}
            <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 -translate-x-1/2 w-px bg-gradient-to-b from-blue-200 via-slate-200 to-amber-200" aria-hidden="true" />
            {STEPS.map((step, idx) => {
              const Icon = step.icon;
              const isEven = idx % 2 === 1;
              return (
                <article
                  key={step.n}
                  className="relative grid lg:grid-cols-2 gap-6 lg:gap-12 items-center"
                  data-testid={`how-it-works-step-${step.n}`}
                >
                  {/* Visual */}
                  <div className={isEven ? "lg:order-2" : ""}>
                    <div className={`rounded-3xl p-8 lg:p-10 bg-gradient-to-br ${step.accent} text-white shadow-xl`}>
                      <div className="flex items-center justify-between mb-6">
                        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-white/80">
                          Step {step.n}
                        </span>
                        <div className="bg-white/15 backdrop-blur rounded-full p-2.5">
                          <Icon className="h-7 w-7 text-white" />
                        </div>
                      </div>
                      <h3 className="text-2xl lg:text-3xl font-bold leading-tight mb-4">
                        {step.title}
                      </h3>
                      <p className="text-white/90 leading-relaxed">{step.blurb}</p>
                    </div>
                  </div>

                  {/* Text */}
                  <div className={isEven ? "lg:order-1 lg:text-right" : ""}>
                    <ul className={`space-y-3 ${isEven ? "lg:items-end lg:flex lg:flex-col" : ""}`}>
                      {step.bullets.map((b) => (
                        <li
                          key={b}
                          className="flex items-start gap-2 text-gray-700"
                        >
                          <CheckCircle2 className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                          <span className="leading-relaxed">{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* The package */}
      <section className="py-16 bg-white border-y border-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <Badge variant="outline" className="border-blue-200 text-blue-700 mb-3">
                <QrCode className="h-3.5 w-3.5 mr-1.5" /> The package
              </Badge>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 leading-snug">
                One handshake-equivalent, even when no one is at the counter.
              </h2>
              <p className="text-gray-600 leading-relaxed text-lg mb-4">
                The Inter-View application package is the printable artifact a candidate
                walks into a business with. Cover letter on page one, resume on page two,
                a QR code on the cover that opens a 60-second video introduction the
                hiring manager can watch later.
              </p>
              <p className="text-gray-600 leading-relaxed text-lg">
                It's the same content a recruiter would expect to receive online, packaged
                so it works in the real world — across a counter, on a coffee shop bulletin
                board, or stapled to a window-front job posting.
              </p>
            </div>
            <div className="relative">
              <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 p-8 shadow-2xl">
                <div className="bg-white rounded-lg p-6 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1.5">
                      <div className="h-2 bg-slate-300 rounded w-32" />
                      <div className="h-2 bg-slate-200 rounded w-24" />
                    </div>
                    <div className="bg-slate-900 p-2 rounded">
                      <QrCode className="h-7 w-7 text-white" />
                    </div>
                  </div>
                  <div className="border-t border-slate-100 pt-3 space-y-1.5">
                    <div className="h-2 bg-slate-200 rounded w-full" />
                    <div className="h-2 bg-slate-200 rounded w-5/6" />
                    <div className="h-2 bg-slate-200 rounded w-4/6" />
                    <div className="h-2 bg-slate-200 rounded w-full" />
                    <div className="h-2 bg-slate-200 rounded w-3/4" />
                  </div>
                  <div className="border-t border-slate-100 pt-3 flex justify-between text-xs text-slate-400 font-medium">
                    <span>Cover Letter</span>
                    <span>Page 1 of 2</span>
                  </div>
                </div>
                <p className="text-xs text-slate-400 text-center mt-4 uppercase tracking-[0.2em] font-medium">
                  Branded printable package
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            Ready to make a stronger first impression?
          </h2>
          <p className="text-lg text-blue-100 mb-8 leading-relaxed">
            Build your Inter-View package in about ten minutes — and walk into your next
            application with the complete version of you, already in their hands.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/signup">
              <Button
                size="lg"
                className="w-full sm:w-auto bg-white text-blue-700 hover:bg-blue-50 text-base px-8 shadow-lg"
                data-testid="how-it-works-cta-signup"
              >
                Build my package
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
            <Link to="/about">
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto border-white/60 text-white hover:bg-white/10 text-base px-8"
              >
                Read our story
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
